/**
 * MobileTorrentEngine — React Native bridge to native libtorrent.
 *
 * This TypeScript layer bridges the canonical TorrentEngine interface
 * to the native Kotlin (Android) and Objective-C++ (iOS) implementations.
 *
 * The native modules handle:
 *  - libtorrent4j (Android) / libtorrent C++ (iOS)
 *  - Torrent creation, piece hashing, disk I/O
 *  - DHT, PEX, TCP/uTP, NAT traversal
 *  - Fast-resume data (save/restore)
 *  - Alert coalescing (~300ms)
 *
 * This bridge handles:
 *  - NativeModules / NativeEventEmitter wiring
 *  - Mapping native events to TorrentEvent
 *  - Ensuring actual byte counters are forwarded (never progress * total)
 *
 * Networking note:
 *  - Android/iOS use TCP/uTP/DHT (standard BitTorrent)
 *  - enableWebTorrent=true is REQUIRED for Web↔Mobile interop
 */

import type {
  TorrentEngine,
  TorrentEngineConfig,
  TorrentInput,
  TorrentSource,
  TorrentDestination,
  TorrentHandle,
  TorrentStatus,
  TorrentEvent,
  TorrentEventListener,
  Unsubscribe,
} from '../torrent-engine';
import { buildMagnetUri } from '../../../protocol/magnet-uri';

// Safe native module access — resolves at runtime on React Native
const NativeModules = (globalThis as any).NativeModules || {};
const NativeEventEmitterClass = (globalThis as any).NativeEventEmitter;
const TorrentEngineNative = NativeModules.TorrentEngineModule;

// Event emitter wrapping the native module
let emitter: any = null;
try {
  if (TorrentEngineNative && NativeEventEmitterClass) {
    emitter = new NativeEventEmitterClass(TorrentEngineNative);
  }
} catch {
  // Not available in web/node test environment
}

function encodeBase64(bytes: Uint8Array): string {
  if (typeof btoa === 'function') {
    let binary = '';
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  const BufferCtor = (globalThis as any).Buffer;
  if (BufferCtor) {
    return BufferCtor.from(bytes).toString('base64');
  }
  throw new Error('Base64 encoder unavailable');
}

export class MobileTorrentEngine implements TorrentEngine {
  private listeners = new Set<TorrentEventListener>();
  private subscriptions: any[] = [];

  configure(config: TorrentEngineConfig): void {
    if (!TorrentEngineNative) {
      console.warn('[MobileTorrentEngine] Native module not available');
      return;
    }

    TorrentEngineNative.configure({
      maxConnections: config.maxConnections ?? 200,
      maxUploadRate: config.maxUploadRateBytesPerSec ?? 0,
      maxDownloadRate: config.maxDownloadRateBytesPerSec ?? 0,
      enableWebTorrent: config.enableWebTorrent ?? true, // REQUIRED for Web↔Mobile
    });

    this._bindNativeEvents();
  }

  async createAndSeed(input: TorrentInput): Promise<TorrentHandle> {
    if (!TorrentEngineNative) {
      throw new Error('Native torrent engine not available');
    }

    const { files } = input;
    if (files.length === 0) throw new Error('No files to seed');

    // Mobile requires native file paths
    const filePath = files[0].nativePath;
    if (!filePath) {
      throw new Error(`File "${files[0].name}" has no native path`);
    }

    const v1Hash: string = await TorrentEngineNative.createAndSeed(filePath);

    return {
      engineTorrentId: v1Hash,
      torrentIdentity: { v1: v1Hash },              // libtorrent gives us v1 directly
      magnetUri: buildMagnetUri({ infoHash: v1Hash }),  // minimal magnet (native bridge)
    };
  }

  async download(
    source: TorrentSource,
    destination: TorrentDestination
  ): Promise<TorrentHandle> {
    if (!TorrentEngineNative) {
      throw new Error('Native torrent engine not available');
    }

    if (destination.kind !== 'native-path') {
      throw new Error('MobileTorrentEngine requires native-path destination');
    }

    const magnet =
      source.magnetUri ??
      (source.infoHash?.v1
        ? buildMagnetUri({ infoHash: source.infoHash.v1 })
        : null);

    if (!magnet) {
      throw new Error('TorrentSource must provide magnetUri or infoHash');
    }

    const hash: string = await TorrentEngineNative.download(
      magnet,
      destination.path
    );
    const id = hash || source.infoHash?.v1 || '';

    return {
      engineTorrentId: id,
      torrentIdentity: { v1: id },
      magnetUri: magnet,
    };
  }

  async pause(id: string): Promise<void> {
    TorrentEngineNative?.pause(id);
  }

  async resume(id: string): Promise<void> {
    TorrentEngineNative?.resume(id);
  }

  async stop(id: string): Promise<void> {
    // Stop = pause + retain data
    TorrentEngineNative?.stop(id);
  }

  async remove(id: string, opts: { deleteData: boolean }): Promise<void> {
    await TorrentEngineNative?.remove(id, opts.deleteData);
  }

  async getStatus(_id: string): Promise<TorrentStatus> {
    // Status comes via events — no synchronous native query
    throw new Error(
      'Use onEvent() for status updates. Native engine does not support synchronous getStatus().'
    );
  }

  async saveResumeData(id: string): Promise<Uint8Array | null> {
    // Triggers native to emit torrent:resume_data event
    await TorrentEngineNative?.saveResumeData(id);
    return null; // data arrives via event
  }

  async restoreFromResumeData(
    resumeData: Uint8Array,
    destination: TorrentDestination
  ): Promise<TorrentHandle> {
    if (!TorrentEngineNative) {
      throw new Error('Native torrent engine not available');
    }
    if (destination.kind !== 'native-path') {
      throw new Error('MobileTorrentEngine requires native-path destination');
    }

    // Convert Uint8Array to base64 for native bridge
    const b64 = encodeBase64(resumeData);
    const hash: string = await TorrentEngineNative.restoreFromResumeData(
      b64,
      destination.path
    );

    return {
      engineTorrentId: hash,
      torrentIdentity: { v1: hash },
      magnetUri: buildMagnetUri({ infoHash: hash }),
    };
  }

  onEvent(listener: TorrentEventListener): Unsubscribe {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  async destroy(): Promise<void> {
    this.subscriptions.forEach((s) => s.remove());
    this.subscriptions = [];
    this.listeners.clear();
  }

  // ─── Private: Native event binding ──────────────────────────────

  private _bindNativeEvents(): void {
    if (!emitter) return;

    // Progress events — native already coalesces at ~300ms
    this.subscriptions.push(
      emitter.addListener('torrent:progress', (e: any) => {
        this._emit({
          type: 'torrent:progress',
          id: e.id,
          timestamp: Date.now(),
          status: {
            id: e.id,
            state: 'downloading',
            progress: e.progress,
            downloadSpeed: e.downloadSpeed, // actual bytes/s from libtorrent
            uploadSpeed: e.uploadSpeed, // actual bytes/s from libtorrent
            downloaded: e.downloaded, // actual verified bytes (NOT progress * total)
            uploaded: e.uploaded, // actual bytes uploaded
            peers: e.peers ?? 0,
            seeds: e.seeds ?? 0,
            eta: e.eta < 0 ? Infinity : e.eta,
            totalBytes: e.totalBytes ?? 0,
            magnetUri: buildMagnetUri({ infoHash: e.id }),
            files: [],
          },
        });
      })
    );

    // Torrent completed — engine auto-seeds after this
    this.subscriptions.push(
      emitter.addListener('torrent:completed', (e: any) => {
        this._emit({
          type: 'torrent:completed',
          id: e.id,
          timestamp: Date.now(),
          status: e.status ?? {
            id: e.id,
            state: 'seeding',
            progress: 1,
            downloadSpeed: 0,
            uploadSpeed: 0,
            downloaded: e.totalBytes ?? 0,
            uploaded: 0,
            peers: e.peers ?? 0,
            seeds: e.seeds ?? 0,
            eta: 0,
            totalBytes: e.totalBytes ?? 0,
            magnetUri: buildMagnetUri({ infoHash: e.id }),
            files: [],
          },
        });
      })
    );

    // Resume data saved — persist to SQLite via coordinator
    this.subscriptions.push(
      emitter.addListener('torrent:resume_data', (e: any) => {
        this._emit({
          type: 'torrent:resume_data',
          id: e.id,
          timestamp: Date.now(),
          resumeDataBase64: e.resumeDataBase64,
        });
      })
    );

    // Error
    this.subscriptions.push(
      emitter.addListener('torrent:error', (e: any) => {
        this._emit({
          type: 'torrent:error',
          id: e.id,
          timestamp: Date.now(),
          error: e.error,
        });
      })
    );

    // Paused
    this.subscriptions.push(
      emitter.addListener('torrent:paused', (e: any) => {
        this._emit({
          type: 'torrent:paused',
          id: e.id,
          timestamp: Date.now(),
        });
      })
    );

    // Metadata received
    this.subscriptions.push(
      emitter.addListener('torrent:metadata', (e: any) => {
        this._emit({
          type: 'torrent:metadata',
          id: e.id,
          timestamp: Date.now(),
        });
      })
    );
  }

  private _emit(event: TorrentEvent): void {
    this.listeners.forEach((l) => {
      try {
        l(event);
      } catch {
        // listener errors must not propagate
      }
    });
  }
}
